import { useCallback, useEffect } from 'react';

export interface Speech {
  /** The browser can read aloud (speechSynthesis). */
  supported: boolean;
  speak: (text: string, language: string) => void;
  cancel: () => void;
}

/**
 * Reads text aloud with the browser's own speech synthesis (UC-11/UC-16): on
 * the device, no server and no credits. Unsupported browsers simply get
 * nothing read — every text is on screen anyway.
 */
export function useSpeech(): Speech {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined';

  const cancel = useCallback(() => {
    if (supported) window.speechSynthesis.cancel();
  }, [supported]);

  const speak = useCallback(
    (text: string, language: string) => {
      if (!supported || text.trim() === '') return;
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = language;
      utterance.rate = 0.95;
      window.speechSynthesis.speak(utterance);
    },
    [supported],
  );

  // Nothing keeps talking after the page is left.
  useEffect(() => cancel, [cancel]);

  return { supported, speak, cancel };
}
